// The three pillar summary cards — `/`'s hand-off to `/series`, `/fields` and `/community`. Summary depth on purpose: the exhaustive per-pillar copy is those three pages' job, and writing it twice is the waste this split exists to avoid.
// Node-prerender-safe (same rule as splash-chrome.tsx): MUI + react-router + Setup/Mui only.
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { featureColors } from '../../src/ui/Setup/Mui';
import { SectionHeader } from './splash-sections';

interface BentoCardProps {
  title: string;
  description: ReactNode;
  icon: ReactNode;
  accentColor: string;
  footer?: ReactNode;
}

/** The pillar bento card. The flex spacer (`mt: 'auto'`) sits on the `footer` rather than on the icon, so the "Everything about X →" link — not the prose — is what pins to the bottom of an uneven row. */
function BentoCard({ title, description, icon, accentColor, footer }: BentoCardProps) {
  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Card sx={{
        height: '100%',
        minHeight: { xs: 'auto', md: 240 },
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '24px',
        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.08)',
        borderTop: '4px solid',
        borderColor: accentColor,
        transition: 'transform .2s ease, box-shadow .2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.12)',
        }
      }}>
        <Box sx={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 150,
          height: 150,
          background: `radial-gradient(circle, ${accentColor} 0%, transparent 60%)`,
          opacity: 0.1,
          borderRadius: '50%',
        }} />
        <Box sx={{
          p: 2,
          borderRadius: 3,
          display: 'inline-flex',
          bgcolor: `${accentColor}15`,
          color: accentColor,
          width: 'fit-content'
        }}>
          {icon}
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </Box>
        {footer && <Box sx={{ mt: 'auto', pt: 3 }}>{footer}</Box>}
      </Card>
    </Grid>
  );
}

const iconSx = { fontSize: 32 };

function Everything({ label, to }: { label: string; to: string }) {
  return (
    <Button component={RouterLink} to={to} endIcon={<ArrowForwardIcon />} sx={{ px: 0, fontWeight: 600 }}>
      Everything about {label}
    </Button>
  );
}

export function HomePillars() {
  return (
    <>
      <SectionHeader
        eyebrow="Where to start"
        title="Three pillars, one journal"
        deck="Each one stands on its own, and each one makes the others better. Start with whichever you came for."
      />
      <Grid container spacing={4}>
        <BentoCard
          accentColor={featureColors.prompt}
          icon={<MenuBookIcon sx={iconSx} />}
          title="Series"
          footer={<Everything label="Series" to="/series" />}
          description="A Series is an ongoing book written out of your real entries. Pick a genre - Fantasy, Sci-Fi, Literary - or an adaptive nonfiction arc that reads how you write and responds in kind. Stay active and Gnothi authors Chapters: generated installments, saved back as entries, that grow richer over time. Arc memory carries context between sessions: the themes your life keeps circling accumulate into a Ledger the arc keeps drawing on, joined on a fiction Series by its characters, events and places. Every Chapter narrates itself into an episode on a real podcast feed. And you hold the pen the whole way - steer the arc, or let it run."
        />
        <BentoCard
          accentColor={featureColors.behavior}
          icon={<InsightsOutlinedIcon sx={iconSx} />}
          title="Fields"
          footer={<Everything label="Fields" to="/fields" />}
          description="Fields are whatever you decide is worth tracking: mood, sleep, pages written, sets lifted, a nested outline of a project with due dates and file uploads. Scoring is deliberately light - the Habitica author's own correction to Habitica, because a streak you are afraid to break has stopped being information. What Fields really buy you is the causal layer: a graph that estimates which of your fields actually move the others over time, and a plain-language read of what it found. Then, for a Series drawing on your whole journal, what you track becomes context the author reads."
        />
        <BentoCard
          accentColor={featureColors.themes}
          icon={<PeopleOutlinedIcon sx={iconSx} />}
          title="Community"
          footer={<Everything label="Community" to="/community" />}
          description="Share a slice of your journal with people you choose - selected by tag, never wholesale - and read what they share back. Three privacy tiers set how much of an entry a reader, and a reader's AI, ever sees. Groups gather several people around one thread, with anonymity as a first-class role rather than a buried setting. Entries shared with you fold into your own Series, so your arc carries a perspective that is not only yours. Run a DnD table on it, or share with a partner and let each arc hold both of you."
        />
      </Grid>
    </>
  );
}
